"use client";
import { TimelineContent } from "@/components/ui/timeline-animation";
import { VerticalCutReveal } from "@/components/ui/vertical-cut-reveal";
import { Button } from "@/components/ui/button";
import { Facebook, Linkedin, ArrowRight, Sun, Zap, Shield, Award, Users, MapPin } from "lucide-react";
import { useRef } from "react";
import { track } from "@/lib/analytics";
import { openOpenSolar as openSolarTool } from "@/lib/widgets";
import heroSolar from "@/assets/hero-solar.jpg";

export default function AboutSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  
  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.3,
        duration: 0.6,
        ease: "easeOut"
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };
  
  const scaleVariants = {
    visible: (i: number) => ({
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        delay: i * 0.3,
        duration: 0.6,
        ease: "easeOut"
      },
    }),
    hidden: {
      filter: "blur(10px)",
      opacity: 0,
      scale: 0.95,
    },
  };

  const handleSocialClick = (platform: string, url: string) => {
    track("contact", { method: platform });
    window.open(url, "_blank", "noopener noreferrer");
  };

  const handleQuoteClick = () => {
    track("generate_lead", { source: "about_page" });
    openSolarTool();
  };

  const handleConsultationClick = () => {
    track("generate_lead", { source: "about_page_consultation" });
    (window as any).Calendly?.initPopupWidget({
      url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
    });
  };

  return (
    <section className="py-8 px-4 bg-muted/30" ref={heroRef}>
      <div className="max-w-6xl mx-auto">
        <div className="relative">
          {/* Header with social icons */}
          <div className="flex justify-between items-center mb-8 w-[85%] absolute lg:top-4 md:top-0 sm:-top-2 -top-3 z-10">
            <div className="flex items-center gap-2 text-xl">
              <span className="text-primary animate-spin text-lg">✱</span>
              <TimelineContent
                as="span"
                animationNum={0}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="text-sm font-medium text-muted-foreground"
              >
                OVER SOL-VI
              </TimelineContent>
            </div>
            <div className="flex gap-4">
              <TimelineContent
                as="button"
                animationNum={0}
                timelineRef={heroRef}
                customVariants={revealVariants}
                onClick={() => handleSocialClick("facebook", "https://www.facebook.com/profile.php?id=61565954054747")}
                className="md:w-8 md:h-8 sm:w-6 w-5 sm:h-6 h-5 border border-border bg-card rounded-lg flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
              >
                <Facebook className="h-4 w-4" />
              </TimelineContent>
              <TimelineContent
                as="button"
                animationNum={1}
                timelineRef={heroRef}
                customVariants={revealVariants}
                onClick={() => handleSocialClick("linkedin", "https://www.linkedin.com/company/sol-vi/")}
                className="md:w-8 md:h-8 sm:w-6 w-5 sm:h-6 h-5 border border-border bg-card rounded-lg flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
              >
                <Linkedin className="h-4 w-4" />
              </TimelineContent>
            </div>
          </div>

          <TimelineContent
            as="figure"
            animationNum={4}
            timelineRef={heroRef}
            customVariants={scaleVariants}
            className="relative group"
          >
            <svg
              className="w-full"
              width="100%"
              height="100%"
              viewBox="0 0 100 40"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <clipPath
                  id="clip-inverted"
                  clipPathUnits="objectBoundingBox"
                >
                  <path
                    d="M0.0998072 1H0.422076H0.749756C0.767072 1 0.774207 0.961783 0.77561 0.942675V0.807325C0.777053 0.743631 0.791844 0.731953 0.799059 0.734076H0.969813C0.996268 0.730255 1.00088 0.693206 0.999875 0.675159V0.0700637C0.999875 0.0254777 0.985045 0.00477707 0.977629 0H0.902473C0.854975 0 0.890448 0.138535 0.850165 0.138535H0.0204424C0.00408849 0.142357 0 0.180467 0 0.199045V0.410828C0 0.449045 0.0136283 0.46603 0.0204424 0.469745H0.0523086C0.0696245 0.471019 0.0735527 0.497877 0.0733523 0.511146V0.915605C0.0723903 0.983121 0.090588 1 0.0998072 1Z"
                    fill="#D9D9D9"
                  />
                </clipPath>
              </defs>
              <image
                clipPath="url(#clip-inverted)"
                preserveAspectRatio="xMidYMid slice"
                width="100%"
                height="100%"
                href={heroSolar}
              />
            </svg>
          </TimelineContent>

          {/* Stats */}
          <div className="flex flex-wrap lg:justify-start justify-between items-center py-3 text-sm">
            <TimelineContent
              as="div"
              animationNum={5}
              timelineRef={heroRef}
              customVariants={revealVariants}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2 mb-2 sm:text-base text-xs">
                <span className="text-primary font-bold">5+</span>
                <span className="text-muted-foreground">jaar expertise</span>
                <span className="text-border">|</span>
              </div>
              <div className="flex items-center gap-2 mb-2 sm:text-base text-xs">
                <span className="text-primary font-bold">1000+</span>
                <span className="text-muted-foreground">tevreden klanten</span>
              </div>
            </TimelineContent>
            <div className="lg:absolute right-0 bottom-16 flex lg:flex-col flex-row-reverse lg:gap-0 gap-4">
              <TimelineContent
                as="div"
                animationNum={6}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="flex lg:text-4xl sm:text-3xl text-2xl items-center gap-2 mb-2"
              >
                <span className="text-primary font-semibold">100%</span>
                <span className="text-muted-foreground uppercase">Service</span>
              </TimelineContent>
              <TimelineContent
                as="div"
                animationNum={7}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="flex items-center gap-2 mb-2 sm:text-base text-xs"
              >
                <span className="text-primary font-bold">15 jaar</span>
                <span className="text-muted-foreground">garantie</span>
                <span className="text-border lg:hidden block">|</span>
              </TimelineContent>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h1 className="sm:text-4xl md:text-5xl text-2xl !leading-[110%] font-semibold text-foreground mb-8">
              <VerticalCutReveal
                splitBy="words"
                staggerDuration={0.1}
                staggerFrom="first"
                reverse={true}
                transition={{
                  type: "spring",
                  stiffness: 250,
                  damping: 30,
                  delay: 3,
                }}
              >
                Zonnekracht voor een vitaal en onafhankelijk leven
              </VerticalCutReveal>
            </h1>

            <TimelineContent
              as="div"
              animationNum={9}
              timelineRef={heroRef}
              customVariants={revealVariants}
              className="grid md:grid-cols-2 gap-8 text-muted-foreground mb-8"
            >
              <TimelineContent
                as="div"
                animationNum={10}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="sm:text-base text-sm"
              >
                <p className="leading-relaxed">
                  Iedereen heeft energie nodig. Maar wat als je die energie niet alleen gebruikt, maar ook slim opwekt, opslaat en deelt? Precies daar draait het bij SOL-VI om.
                </p>
              </TimelineContent>
              <TimelineContent
                as="div"
                animationNum={11}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="sm:text-base text-sm"
              >
                <p className="leading-relaxed">
                  Wij geloven dat duurzame energie niet ingewikkeld hoeft te zijn. Het moet juist toegankelijk, betrouwbaar en rendabel zijn. Door weer en wind, energie die met je meegaat.
                </p>
              </TimelineContent>
            </TimelineContent>

            {/* Brand Story */}
            <TimelineContent
              as="div"
              animationNum={12}
              timelineRef={heroRef}
              customVariants={revealVariants}
              className="bg-card p-6 rounded-lg border mb-8"
            >
              <h3 className="text-xl font-semibold mb-4 text-foreground">Onze naam vertelt ons verhaal</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Sun className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <div className="font-medium text-foreground">SOL</div>
                    <div className="text-sm text-muted-foreground">staat voor de zon – de onuitputtelijke bron van duurzame energie</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="h-6 w-6 text-secondary mt-1" />
                  <div>
                    <div className="font-medium text-foreground">VI</div>
                    <div className="text-sm text-muted-foreground">staat voor vitaliteit, vrijheid en innovatie</div>
                  </div>
                </div>
              </div>
            </TimelineContent>

            {/* Values */}
            <TimelineContent
              as="div"
              animationNum={13}
              timelineRef={heroRef}
              customVariants={revealVariants}
              className="grid sm:grid-cols-3 gap-4 mb-8"
            >
              <div className="text-center p-4 bg-card rounded-lg border">
                <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">Betrouwbaar</h4>
                <p className="text-sm text-muted-foreground">15 jaar garantie op al onze installaties</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg border">
                <Award className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">Kwaliteit</h4>
                <p className="text-sm text-muted-foreground">Alleen A-merk componenten</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg border">
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">Persoonlijk</h4>
                <p className="text-sm text-muted-foreground">Maatwerk voor jouw situatie</p>
              </div>
            </TimelineContent>
          </div>

          <div className="md:col-span-1">
            <div className="text-right">
              <TimelineContent
                as="div"
                animationNum={14}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="text-primary text-2xl font-bold mb-2"
              >
                SOL-VI
              </TimelineContent>
              <TimelineContent
                as="div"
                animationNum={15}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="text-muted-foreground text-sm mb-4"
              >
                Specialist in duurzame energie
              </TimelineContent>
              
              <TimelineContent
                as="div"
                animationNum={16}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="flex items-center justify-end gap-2 text-sm text-muted-foreground mb-8"
              >
                <MapPin className="h-4 w-4" />
                <span>Elst, Gelderland</span>
              </TimelineContent>

              <TimelineContent
                as="div"
                animationNum={17}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="mb-6"
              >
                <p className="text-foreground font-medium mb-4">
                  Klaar om de stap te zetten naar een duurzame toekomst?
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Met SOL-VI krijg je niet alleen techniek, maar ook een belofte. Door weer en wind, energie die met je meegaat.
                </p>
              </TimelineContent>

              <TimelineContent
                as="div"
                animationNum={18}
                timelineRef={heroRef}
                customVariants={revealVariants}
                className="space-y-3"
              >
                <Button
                  onClick={handleQuoteClick}
                  className="w-full bg-neutral-900 hover:bg-neutral-950 shadow-lg shadow-neutral-900/25 border border-neutral-700 flex gap-2 hover:gap-4 transition-all duration-300 ease-in-out text-white font-semibold"
                >
                  GRATIS OFFERTE
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleConsultationClick}
                  variant="outline"
                  className="w-full"
                >
                  PLAN CONSULTATIE
                </Button>
              </TimelineContent>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}